import { Button, Card, Input, Form, Select, Spin, Alert, Space, message } from "antd";
import api from "ApiClient";
import ErrorToDisplay from "components/error-handling/ErrorToDisplay";
import { useCallback, useEffect, useRef, useState } from "react";
import { Prompt } from "react-router";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { updateSchoolContact, setIsEqual } from "redux/reducers/SchoolContactSlice";
import { ApiURL } from "utils/Actions";
import { schoolContactLabel, states, titles } from "utils/AssetsData";
import { API_RESPONSE_STATUS, CONTACT_FIELD, STATE_NAME } from "utils/Constants";
import { patchResetPassword } from "utils/hooks/apiActions";
import { deepEqual, excludeEmptyObj } from "utils/hooks/handleObjAndArray";
import {
  requiredRule,
  onlyNumberRule,
  alphanumericDashRule,
  alphabeticalRule
} from "utils/validators/rules";
import { schoolUser, common } from "utils/Wording";
import { Helmet } from "react-helmet";
import ResetPasswordModal from "./reset-password-modal";
import AuthHelper from "components/auth/helpers";
import { useHistory } from "react-router-dom";
import ReadOnlyValue from "components/common/read-only-value";
import { isEmpty } from "lodash";
import OptionalLabel from "components/common/optional-label";
import { showErrorNotification, showSuccessNotification } from "utils/hooks/handleNotification";
import closeOutlined from "assets/icons/close-outlined.svg";
import infoIcon from "assets/icons/info-icon-blue.svg";
import { useTranslation } from "react-i18next";
import CustomPrompt from "components/common/custom-prompt";
import DeleteAccountModal from "./delete-account-modal";
import UnableToDeleteModal from "./unable-to-delete-modal";
import { allocateOptions } from "utils/hooks/allocateOptions";

const UNSAVED_CHANGES_MESSAGE = "UNSAVED_CHANGES_MESSAGE";

const { Option } = Select;

const transformToUpperCaseFields = [
  CONTACT_FIELD.GIVEN_NAME_1,
  CONTACT_FIELD.GIVEN_NAME_2,
  CONTACT_FIELD.FAMILY_NAME,
  CONTACT_FIELD.POSITION,
  CONTACT_FIELD.ADDRESS_1,
  CONTACT_FIELD.ADDRESS_2,
  CONTACT_FIELD.ADDRESS_3,
  CONTACT_FIELD.AUS_POST_CODE
];

const closeDiscardChangesNotification = () => {
  message.destroy(UNSAVED_CHANGES_MESSAGE);
};

const showUnsavedChangesMsg = () => {
  message.info({
    content: (
      <Space align="center">
        <div style={{ marginRight: 16 }}>This page has unsaved changes</div>
        <Button
          type="text"
          onClick={closeDiscardChangesNotification}
          style={{ color: "white", padding: 0 }}
        >
          <img src={closeOutlined} alt="dismiss notification" />
        </Button>
      </Space>
    ),
    className: "bottom-notification",
    style: {
      marginTop: "90vh"
    },
    icon: <div></div>,
    duration: null,
    key: UNSAVED_CHANGES_MESSAGE
  });
};

export const Settings: React.FC = () => {
  const { t } = useTranslation();
  const showUnsavedChangesMsgTimeoutRef: { current: NodeJS.Timeout | null } = useRef(null);
  const history = useHistory();
  const [form] = Form.useForm();
  const { resetFields } = form;
  const dispatch = useAppDispatch();
  const {
    isLoadingError: isSchoolContactLoadingError,
    isEqual: isSchoolContactEqual,
    isLoading: isSchoolDataLoading,
    settingFieldData: schoolContactData,
    data: initialData
  } = useAppSelector(state => state.schoolContact);
  const isSchoolContactEmpty = isEmpty(initialData);

  const roles = AuthHelper.getRoles();

  const successText = t("myAccount.notifications.success");
  const discardedText = t("myAccount.notifications.discard");
  const errorText = t("myAccount.notifications.error");

  const [currentFormData, setCurrentFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [isOpenUnableToDelete, setIsOpenUnableToDelete] = useState<boolean>(false);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const [asyncError, setAsyncError] = useState([]);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string[]>([]);

  const logOut = useCallback(() => {
    history.push("/");
    AuthHelper.clearAuth();
    history.go(0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById("skip-main-wrap")?.focus();
  }, []);

  useEffect(() => {
    message.config({ maxCount: 1 });
    !isSchoolContactEqual && dispatch(setIsEqual(true));
    asyncError.length > 0 && setAsyncError([]);
    return () => {
      clearTimeout(showUnsavedChangesMsgTimeoutRef.current as NodeJS.Timeout);
      closeDiscardChangesNotification();
    };
  }, []);

  useEffect(() => {
    if (isEmailSent) {
      logOut();
    }
  }, [isEmailSent]);

  const updateBtnStatusInValidation = () => {
    const values = form.getFieldsValue();
    const cleanedValues = excludeEmptyObj(values);
    const resultDeepEqual = deepEqual(cleanedValues, initialData);
    dispatch(setIsEqual(resultDeepEqual));
    const errorList = form.getFieldsError();
    const hasError = errorList.filter(e => e.errors.length > 0).length > 0;
    if (!resultDeepEqual && !hasError) {
      setIsSaveBtnDisabled(false);
    } else {
      setIsSaveBtnDisabled(true);
    }
  };

  useEffect(() => {
    if (!isSaveBtnDisabled) {
      showUnsavedChangesMsgTimeoutRef.current = setTimeout(() => {
        showUnsavedChangesMsg();
      }, 4000);
    }
    return () => {
      if (showUnsavedChangesMsgTimeoutRef.current) {
        clearTimeout(showUnsavedChangesMsgTimeoutRef.current);
        // destroy if already showing
        closeDiscardChangesNotification();
      }
    };
  }, [isSaveBtnDisabled]);

  const onFieldChangeForm = async (changedFields: any[]) => {
    const changedObj: any = {};
    if (Array.isArray(changedFields)) {
      changedFields
        .filter(item => item.errors.length == 0 && item.touched == true)
        .forEach(item => (changedObj[item.name[0]] = item.value));
    }
    setCurrentFormData((prevState: any) => ({
      ...prevState,
      ...changedObj
    }));
    updateBtnStatusInValidation();
  };

  const onAlertClose = () => {
    setAsyncError([]);
  };

  const showResetPasswordModal = () => {
    setIsOpenModal(true);
  };

  const onSendEmail = async () => {
    const defaultMessage = ["An error occurred while sending the request. Please try again."];
    setIsSendingEmail(true);
    try {
      const returnData = await patchResetPassword(initialData.email);
      if (returnData.error) {
        const errorMessage = returnData.errorMsg || defaultMessage;
        setResetPasswordError(errorMessage);
        return false;
      } else {
        setIsEmailSent(true);
      }
    } catch (error) {
      setResetPasswordError(defaultMessage);
      console.error(error);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const onCancelModal = () => {
    setIsOpenModal(false);
    setIsEmailSent(false);
  };

  const handleDiscard = () => {
    resetFields();
    showSuccessNotification({ message: discardedText, placement: "bottomRight" });
    setIsSaveBtnDisabled(true);
  };

  const handleFailingOnSave = () => {
    form.setFieldsValue({
      email: schoolContactData.email
    });

    const changedObj: any = {};
    changedObj["email"] = schoolContactData.email;
    setCurrentFormData((prevState: any) => ({
      ...prevState,
      ...changedObj
    }));

    setIsSaveBtnDisabled(true);
  };

  const handleOnFinish = async (values: any) => {
    const formattedValues: any = {};
    Object.keys(values).forEach(key => {
      if (transformToUpperCaseFields.includes(key)) {
        if (values[key]) {
          if (key == "auPostCode") {
            formattedValues[key] = values[key];
          } else {
            formattedValues[key] = values[key].toUpperCase();
          }
        }
      } else {
        formattedValues[key] = values[key];
      }
    });
    setSubmitting(true);
    form.validateFields().then(async () => {
      try {
        await api
          .patch(ApiURL.schoolContact, formattedValues)
          .then(response => {
            if (response.status > 400 && response.status < 600) {
              throw new Error(common.savingError);
            }
            return response;
          })
          .then(returnedResponse => {
            const { status, data: resData } = returnedResponse.data;
            if (status === API_RESPONSE_STATUS.SUCCESS && resData) {
              dispatch(updateSchoolContact(resData));
              dispatch(setIsEqual(true));
              setIsSaveBtnDisabled(true);
              showSuccessNotification({ message: successText, placement: "bottomRight" });
              setCurrentFormData({});
              onAlertClose();
            }
          })
          .catch(error => {
            if (error.response) {
              const errorMessage = error.response.data.errors;
              setAsyncError(errorMessage);
            }
            handleFailingOnSave();
            return error;
          });
      } catch (error) {
        handleFailingOnSave();
        showErrorNotification({ message: errorText, placement: "bottomRight" });
        return error;
      } finally {
        setSubmitting(false);
      }
    });
  };

  const handleClickDelete = useCallback(() => {
    if (AuthHelper.isAdmin()) {
      setIsOpenUnableToDelete(true);
    } else {
      setIsOpenDeleteModal(true);
    }
  }, []);

  const handleSaveChanges = useCallback(async () => {
    await form.submit();
  }, [form]);

  return (
    <>
      <Helmet>
        <title>{t("helmet.settings.title")}</title>
        <meta name="description" content={t("helmet.settings.meta") || ""} />
      </Helmet>

      <Prompt when={!isSchoolContactEqual} message={schoolUser.unsavedChange} />
      <CustomPrompt
        when={!isSaveBtnDisabled}
        navigate={path => history.push(path)}
        shouldBlockNavigation={() => {
          if (!isSaveBtnDisabled) {
            return true;
          }
          return false;
        }}
        onSaveChanges={handleSaveChanges}
      />

      {/* modals */}
      <ResetPasswordModal
        open={isOpenModal}
        onCancelModal={onCancelModal}
        onSendEmail={onSendEmail}
        email={schoolContactData.email}
        resetPasswordError={resetPasswordError}
        isSendingEmail={isSendingEmail}
      />

      <DeleteAccountModal
        open={isOpenDeleteModal}
        onCancel={() => {
          setIsOpenDeleteModal(false);
        }}
      />
      <UnableToDeleteModal
        open={isOpenUnableToDelete}
        onCancel={() => {
          setIsOpenUnableToDelete(false);
        }}
      />

      {isSchoolContactLoadingError ? (
        <ErrorToDisplay title={common.loadingError} />
      ) : isSchoolDataLoading && isSchoolContactEmpty ? (
        <Spin size="large" className="center-content" style={{ height: "50vh" }} />
      ) : (
        <>
          <h1 className="display-mid-typo">{t("myAccount.heading")}</h1>
          <div className="gap-section">{t("myAccount.description")}</div>
          <Card
            title={
              <h2 className="display-h2-small" style={{ whiteSpace: "break-spaces" }}>
                {t("myAccount.personalInformation.title")}
              </h2>
            }
            style={{ marginBottom: 16 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFieldsChange={onFieldChangeForm}
              initialValues={!isSchoolContactEmpty ? initialData : {}}
              requiredMark={false}
              style={{ width: "100%" }}
              onFinish={handleOnFinish}
            >
              {asyncError.length > 0 &&
                asyncError.map(err => (
                  <Alert
                    type="error"
                    showIcon={true}
                    message={err}
                    closable
                    onClose={onAlertClose}
                    className="gap-section"
                  />
                ))}

              <ReadOnlyValue
                label={schoolContactLabel.school}
                value={schoolContactData?.schoolName}
                capitalize={true}
              />
              <ReadOnlyValue
                label={schoolContactLabel.role}
                value={roles.map(role => t(`roles.${role}`)).join(", ")}
                infoAfter={
                  AuthHelper.isAdmin() ? (
                    <Space>
                      <img src={infoIcon} alt="" />
                      <div className="secondary">
                        Go to the Staff members page to manage your role.
                      </div>
                    </Space>
                  ) : undefined
                }
              />

              <ReadOnlyValue label={schoolContactLabel.username} value={schoolContactData.email} />

              <div className="info-item">
                <div style={{ marginBottom: 12, fontWeight: 500 }}>Password</div>
                <Button onClick={showResetPasswordModal}>
                  {t("myAccount.personalInformation.resetPasswordCTA")}
                </Button>
              </div>

              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.title} />}
                name={CONTACT_FIELD.TITLE}
              >
                <Select getPopupContainer={allocateOptions}>
                  {titles.map(e => (
                    <Option key={e.value} value={e.value}>
                      {e.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                required
                label={schoolContactLabel.firstName}
                name={CONTACT_FIELD.GIVEN_NAME_1}
                rules={[requiredRule, alphabeticalRule]}
              >
                <Input maxLength={15} className="display-as-uppercase" />
              </Form.Item>

              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.givenName2} />}
                name={CONTACT_FIELD.GIVEN_NAME_2}
                rules={[alphabeticalRule]}
              >
                <Input maxLength={15} className="display-as-uppercase" />
              </Form.Item>

              <Form.Item
                required
                label={schoolContactLabel.lastName}
                name={CONTACT_FIELD.FAMILY_NAME}
                rules={[requiredRule, alphabeticalRule]}
              >
                <Input maxLength={20} className="display-as-uppercase" />
              </Form.Item>

              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.position} />}
                name={CONTACT_FIELD.POSITION}
                rules={[alphabeticalRule]}
              >
                <Input maxLength={30} className="display-as-uppercase" />
              </Form.Item>

              {/* all address inputs will be replaced by geoCoding api */}
              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.address1} />}
                name={CONTACT_FIELD.ADDRESS_1}
                rules={[alphanumericDashRule]}
              >
                <Input maxLength={40} className="display-as-uppercase" />
              </Form.Item>

              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.address2} />}
                name={CONTACT_FIELD.ADDRESS_2}
                rules={[alphanumericDashRule]}
              >
                <Input maxLength={30} className="display-as-uppercase" />
              </Form.Item>

              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.address3} />}
                name={CONTACT_FIELD.ADDRESS_3}
              >
                <Select getPopupContainer={allocateOptions}>
                  {states.map(
                    e =>
                      e.value !== STATE_NAME.IB && (
                        <Option key={e.value} value={e.value}>
                          {e.label}
                        </Option>
                      )
                  )}
                </Select>
              </Form.Item>

              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.postCode} />}
                name={CONTACT_FIELD.AUS_POST_CODE}
                rules={[onlyNumberRule]}
              >
                <Input maxLength={4} className="display-as-uppercase" />
              </Form.Item>

              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.phone} />}
                name={CONTACT_FIELD.PHONE}
                rules={[onlyNumberRule]}
              >
                <Input maxLength={15} />
              </Form.Item>

              <Form.Item
                label={<OptionalLabel label={schoolContactLabel.phoneMobile} />}
                name={CONTACT_FIELD.MOBILE}
                rules={[onlyNumberRule]}
              >
                <Input maxLength={15} />
              </Form.Item>

              <Space>
                <Button
                  type="primary"
                  disabled={isSaveBtnDisabled || submitting}
                  htmlType="submit"
                  loading={submitting}
                >
                  {t("myAccount.personalInformation.saveCTA")}
                </Button>
                {!isSaveBtnDisabled && (
                  <Button disabled={isSaveBtnDisabled || submitting} onClick={handleDiscard}>
                    {t("myAccount.personalInformation.discardCTA")}
                  </Button>
                )}
              </Space>
            </Form>
          </Card>

          <Card title={<h2 className="display-h2-small">{t("myAccount.deleteAccount.title")}</h2>}>
            <p>{t("myAccount.deleteAccount.description")}</p>
            <Button type="primary" danger onClick={handleClickDelete}>
              {t("myAccount.deleteAccount.deleteCTA")}
            </Button>
          </Card>
        </>
      )}
    </>
  );
};

export default Settings;
